import { errorInterceptor } from './ErrorInterceptor';

/**
 * Global fetch interceptor to handle authentication errors
 */
class FetchInterceptor {
    private originalFetch: typeof fetch;

    constructor() {
        this.originalFetch = window.fetch;
        this.intercept();
    }

    private intercept() {
        const self = this;
        const boundFetch = self.originalFetch.bind(window);

        window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
            try {
                // Skip interception for Firebase-related URLs to avoid conflicts
                const url = typeof input === 'string' ? input : input.toString();
                if (url.includes('firebase') ||
                    url.includes('googleapis.com') ||
                    url.includes('cloudfunctions.net') ||
                    url.includes('accounts.google.com') ||
                    url.includes('identitytoolkit.googleapis.com') ||
                    url.includes('securetoken.googleapis.com')) {
                    return await boundFetch(input, init);
                }

                // Use the bound fetch function to preserve context
                const response = await boundFetch(input, init);

                // Check for authentication errors
                if (response.status === 401 || response.status === 403) {
                    console.log('[FetchInterceptor] Authentication error detected:', response.status);

                    // Clone the response to read the body
                    const clonedResponse = response.clone();
                    let errorData = null;

                    try {
                        errorData = await clonedResponse.json();
                    } catch {
                        // If we can't parse JSON, create a generic error
                        errorData = {
                            status: response.status,
                            message: response.status === 401 ? 'Unauthorized' : 'Forbidden'
                        };
                    }

                    // Handle the authentication error
                    errorInterceptor.handleError(errorData);

                    // Return the original response so the calling code can handle it
                    return response;
                }

                return response;
            } catch (error) {
                // Handle network errors
                console.error('[FetchInterceptor] Network error:', error);

                // Check if it's an authentication-related error
                if (self.isAuthenticationError(error)) {
                    errorInterceptor.handleError(error);
                }

                throw error;
            }
        };
    }

    private isAuthenticationError(error: any): boolean {
        if (!error) return false;

        // Check for authentication-related error messages
        if (error.message) {
            const authErrorMessages = [
                'unauthorized',
                'authentication failed',
                'invalid token',
                'token expired',
                'session expired'
            ];

            return authErrorMessages.some(msg =>
                error.message.toLowerCase().includes(msg.toLowerCase())
            );
        }

        return false;
    }

    /**
     * Restore original fetch
     */
    restore() {
        window.fetch = this.originalFetch;
    }
}

// Create and export the interceptor instance
export const fetchInterceptor = new FetchInterceptor();

// Export the class for testing purposes
export { FetchInterceptor };
